import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { ReactElement, useMemo, useState } from 'react';
import {
  ListScratchpadPartsFragment,
  Machine,
  Scratchpad,
  ScratchpadSortColumnType,
  SortDirectionType,
  useListScratchpadsQuery,
} from '../../../api/graphql/generated';
import { useSearchListQueryWithInfiniteScroll } from '../../../api/hooks/infinite-scroll.hooks';
import { LoadingRetry } from '../../graphql/LoadingRetry';
import { BottomPanel } from '../../ui/panels/bottom-panel';
import { ActiveFilterPopoverContent } from '../../ui/searchbar/active-filter-popover-content';
import {
  Searchbar,
  SearchbarPopoverChildrenProps,
} from '../../ui/searchbar/searchbar';
import { useScratchpadPlaylistPartsContext } from '../playlist/scratchpad-playlist.hooks';
import { ScratchpadCard } from './scratchpad-card';
import { ScratchpadItemDetail } from './scratchpad-item-detail';
import { ScratchpadNoParts } from './scratchpad-no-parts';
import { ScratchpadNoPartsFiltered } from './scratchpad-no-parts-filtered';
import './scratchpad.css';

interface ScratchpadListProps {
  teamId: string;
  machine?: Machine;
}

const ScratchpadList = (props: ScratchpadListProps): ReactElement => {
  const { machine, teamId } = props;

  const [selectedScratchpad, setSelectedScratchpad] = useState<
    Partial<Scratchpad> | undefined
  >();

  const [showActive, setShowActive] = useState(true);
  const [shouldPoll, setShouldPoll] = useState(false);

  const { search, infiniteScroll, searchTerm, result } =
    useSearchListQueryWithInfiniteScroll({
      useQuery: useListScratchpadsQuery,
      queryOptions: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        skip: machine?.id === undefined,
        pollInterval: shouldPoll ? 10_000 : 0,
      },
      input: {
        machineId: machine?.id as string,
        active: showActive,
        sortColumn: ScratchpadSortColumnType.NAME,
        sortDirection: SortDirectionType.ASC,
      },
    });

  const { data, loading, refetch } = result;

  useIonViewWillEnter(() => {
    setShouldPoll(true);
  });

  useIonViewWillLeave(() => {
    setShouldPoll(false);
  });

  const scratchpads: ListScratchpadPartsFragment[] = useMemo(() => {
    return data?.listScratchpads.items ?? [];
  }, [data?.listScratchpads.items]);

  // NOTE: technically there is always a filter since active is by default true
  //  to truly know whether there are no results, we need to know the total count without a filter from the api
  const hasFilter = useMemo(() => {
    return searchTerm !== undefined || !showActive;
  }, [searchTerm, showActive]);

  const filterPopoverContent = (props: SearchbarPopoverChildrenProps) => {
    return (
      <ActiveFilterPopoverContent
        showActive={showActive}
        onActiveChange={(active) => {
          setShowActive(active);
          // eslint-disable-next-line react/prop-types
          props.close();
        }}
      />
    );
  };

  const statuses = useScratchpadPlaylistPartsContext();

  const noResultsComponent = useMemo(() => {
    return hasFilter ? <ScratchpadNoPartsFiltered /> : <ScratchpadNoParts />;
  }, [hasFilter]);

  return (
    <div className="machine-list-container">
      <BottomPanel
        show={selectedScratchpad !== undefined}
        onDidDismiss={() => {
          setSelectedScratchpad(undefined);
        }}
      >
        {({ onClose }) => (
          <ScratchpadItemDetail
            onClose={onClose}
            scratchpad={selectedScratchpad}
            teamId={teamId}
          />
        )}
      </BottomPanel>
      <Searchbar
        onSearch={search}
        filterPopoverContent={filterPopoverContent}
        showFilterIndicator={showActive === false}
      />
      <LoadingRetry
        loading={loading}
        refetch={refetch}
        results={{
          hasResults: scratchpads.length > 0,
          noResultsComponent,
        }}
      >
        <>
          {scratchpads.map((scratchpad) => {
            const status = statuses[scratchpad.id];

            return (
              <div key={scratchpad.id} className="machine-list-spacing">
                <ScratchpadCard
                  scratchpad={scratchpad}
                  onClick={setSelectedScratchpad}
                  status={status}
                />
              </div>
            );
          })}
          {infiniteScroll}
        </>
      </LoadingRetry>
    </div>
  );
};

export { ScratchpadList };
